/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $deleteTableColumn,
  $getElementGridForTableNode,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn,
  $insertTableRow,
  $isTableCellNode,
  $isTableRowNode,
  $removeTableRowAtIndex,
  getTableSelectionFromTableElement,
  HTMLTableElementWithWithTableSelectionState,
  TableCellHeaderStates,
  TableCellNode,
} from '@lexical/table';
import { TFunction } from 'i18next';
import {
  $getSelection,
  DEPRECATED_$isGridSelection,
  $isRangeSelection,
  $setSelection,
} from 'lexical';
import * as React from 'react';
import { ReactPortal, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  $fillMissingCellWidths,
  $sizedColumnWidthAt,
} from '../utils/tableWidthLexical';

type TableCellActionMenuProps = Readonly<{
  contextRef: { current: null | HTMLElement };
  onClose: () => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  tableCellNode: TableCellNode;
  t: TFunction;
}>;

function TableActionMenu({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
  t,
}: TableCellActionMenuProps) {
  const [editor] = useLexicalComposerContext();
  const dropDownRef = useRef<HTMLDivElement | null>(null);
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  });

  useEffect(() => {
    return editor.registerMutationListener(TableCellNode, (nodeMutations) => {
      const nodeUpdated =
        nodeMutations.get(tableCellNode.getKey()) === 'updated';

      if (nodeUpdated) {
        editor.getEditorState().read(() => {
          updateTableCellNode(tableCellNode.getLatest());
        });
      }
    });
  }, [editor, tableCellNode]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if (DEPRECATED_$isGridSelection(selection)) {
        const selectionShape = selection.getShape();

        updateSelectionCounts({
          columns: selectionShape.toX - selectionShape.fromX + 1,
          rows: selectionShape.toY - selectionShape.fromY + 1,
        });
      }
    });
  }, [editor]);

  useEffect(() => {
    const menuButtonElement = contextRef.current;
    const dropDownElement = dropDownRef.current;

    if (menuButtonElement != null && dropDownElement != null) {
      const menuButtonRect = menuButtonElement.getBoundingClientRect();

      dropDownElement.style.opacity = '1';

      dropDownElement.style.left = `${
        menuButtonRect.left + menuButtonRect.width + window.pageXOffset + 5
      }px`;

      dropDownElement.style.top = `${
        menuButtonRect.top + window.pageYOffset
      }px`;

      // 4줄로 줄이며 폭이 넓어졌다 — 화면 오른쪽·아래를 넘으면 단추 왼쪽·위로 당긴다
      const MARGIN = 8;
      const menuRect = dropDownElement.getBoundingClientRect();
      if (menuRect.right > window.innerWidth - MARGIN) {
        dropDownElement.style.left = `${Math.max(
          MARGIN,
          menuButtonRect.left - menuRect.width - 5
        ) + window.pageXOffset}px`;
      }
      if (menuRect.bottom > window.innerHeight - MARGIN) {
        dropDownElement.style.top = `${Math.max(
          MARGIN,
          window.innerHeight - MARGIN - menuRect.height
        ) + window.pageYOffset}px`;
      }
    }
  }, [contextRef, dropDownRef]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        !dropDownRef.current.contains(event.target as Node) &&
        !contextRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    window.addEventListener('click', handleClickOutside);

    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = editor.getElementByKey(
          tableNode.getKey()
        ) as HTMLTableElementWithWithTableSelectionState;

        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM');
        }

        const tableSelection = getTableSelectionFromTableElement(tableElement);
        if (tableSelection !== null) {
          tableSelection.clearHighlight();
        }

        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }

      $setSelection(null);
    });
  }, [editor, tableCellNode]);

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        const selection = $getSelection();

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        let tableRowIndex;

        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableRowIndex = shouldInsertAfter
            ? selectionShape.toY
            : selectionShape.fromY;
        } else {
          tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
        }

        const grid = $getElementGridForTableNode(editor, tableNode);

        // Lexical 은 새 줄 칸에 위·아래 칸의 폭을 베끼는데, 한 줄짜리 표 위로 넣으면 베낄 칸이 없어 첫 줄이
        // 폭을 잃고 표가 꽉 찬 폭으로 돌아간다(utils/tableWidth.ts). 크기가 정해진 표면 빈 폭을 채운다.
        const fallbackWidth = $sizedColumnWidthAt(tableNode, tableCellNode);

        $insertTableRow(
          tableNode,
          tableRowIndex,
          shouldInsertAfter,
          selectionCounts.rows,
          grid
        );

        if (fallbackWidth != null) {
          $fillMissingCellWidths(tableNode, fallbackWidth);
        }

        clearTableSelection();

        onClose();
      });
    },
    [editor, tableCellNode, selectionCounts.rows, clearTableSelection, onClose]
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        const selection = $getSelection();

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        let tableColumnIndex;

        if (DEPRECATED_$isGridSelection(selection)) {
          const selectionShape = selection.getShape();
          tableColumnIndex = shouldInsertAfter
            ? selectionShape.toX
            : selectionShape.fromX;
        } else {
          tableColumnIndex =
            $getTableColumnIndexFromTableCellNode(tableCellNode);
        }

        const grid = $getElementGridForTableNode(editor, tableNode);

        // 크기가 정해진 표면 넣기 전에 고른 열의 폭을 읽어 둔다 — Lexical 은 새 칸에 폭을 주지 않아,
        // 그대로 두면 표가 꽉 찬 폭으로 돌아간다(utils/tableWidth.ts). 새 열은 고른 열과 같은 폭으로 선다.
        const newColumnWidth = $sizedColumnWidthAt(tableNode, tableCellNode);

        $insertTableColumn(
          tableNode,
          tableColumnIndex,
          shouldInsertAfter,
          selectionCounts.columns,
          grid
        );

        if (newColumnWidth != null) {
          $fillMissingCellWidths(tableNode, newColumnWidth);
        }

        clearTableSelection();

        onClose();
      });
    },
    [
      editor,
      tableCellNode,
      selectionCounts.columns,
      clearTableSelection,
      onClose,
    ]
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

      $removeTableRowAtIndex(tableNode, tableRowIndex);

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableColumnIndex =
        $getTableColumnIndexFromTableCellNode(tableCellNode);

      $deleteTableColumn(tableNode, tableColumnIndex);

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableRowIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

      const tableRows = tableNode.getChildren();

      if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }

      const tableRow = tableRows[tableRowIndex];

      if (!$isTableRowNode(tableRow)) {
        throw new Error('Expected table row');
      }

      tableRow.getChildren().forEach((tableCell) => {
        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }

        tableCell.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableColumnIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableColumnIndex =
        $getTableColumnIndexFromTableCellNode(tableCellNode);

      const tableRows = tableNode.getChildren();

      for (let r = 0; r < tableRows.length; r++) {
        const tableRow = tableRows[r];

        if (!$isTableRowNode(tableRow)) {
          throw new Error('Expected table row');
        }

        const tableCells = tableRow.getChildren();

        if (tableColumnIndex >= tableCells.length || tableColumnIndex < 0) {
          throw new Error('Expected table cell to be inside of table row.');
        }

        const tableCell = tableCells[tableColumnIndex];

        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }

        tableCell.toggleHeaderStyle(TableCellHeaderStates.COLUMN);
      }

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const rowCount = selectionCounts.rows;
  const columnCount = selectionCounts.columns;
  const hasRowHeader =
    (tableCellNode.__headerState & TableCellHeaderStates.ROW) ===
    TableCellHeaderStates.ROW;
  const hasColumnHeader =
    (tableCellNode.__headerState & TableCellHeaderStates.COLUMN) ===
    TableCellHeaderStates.COLUMN;
  const tl = (key: string) => t(`toolbar:tableActionMenuPlugin.${key}`);
  // 단추는 짧은 낱말이고, 종전 한 줄짜리 문장(「Insert 2 rows above」)은 마우스를 올리면 보인다
  const insertTitle = (count: number, one: string, many: string, where: string) =>
    `${tl('Insert')} ${count === 1 ? tl(one) : `${count} ${tl(many)}`} ${tl(where)}`;
  const headerTitle = (on: boolean, key: string) =>
    `${on ? t('action:Remove') : t('action:Add')} ${tl(key)}`;

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="dropdown"
      ref={dropDownRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {/* 9줄(208×343, 2026-10-06 실측) → 대상별 4줄. 동작 함수는 그대로, 배치만 바꿨다 */}
      <div className="flex flex-col gap-1 p-2 text-sm">
        <MenuLine label={tl('row')} count={rowCount}>
          <MenuButton
            title={insertTitle(rowCount, 'row', 'rows', 'above')}
            onClick={() => insertTableRowAtSelection(false)}
          >
            {upperFirst(tl('above'))}
          </MenuButton>
          <MenuButton
            title={insertTitle(rowCount, 'row', 'rows', 'below')}
            onClick={() => insertTableRowAtSelection(true)}
          >
            {upperFirst(tl('below'))}
          </MenuButton>
          <MenuButton
            danger
            title={tl('Delete_row')}
            onClick={() => deleteTableRowAtSelection()}
          >
            {t('action:Delete')}
          </MenuButton>
        </MenuLine>
        <MenuLine label={tl('column')} count={columnCount}>
          <MenuButton
            title={insertTitle(columnCount, 'column', 'columns', 'left')}
            onClick={() => insertTableColumnAtSelection(false)}
          >
            {upperFirst(tl('left'))}
          </MenuButton>
          <MenuButton
            title={insertTitle(columnCount, 'column', 'columns', 'right')}
            onClick={() => insertTableColumnAtSelection(true)}
          >
            {upperFirst(tl('right'))}
          </MenuButton>
          <MenuButton
            danger
            title={tl('Delete_column')}
            onClick={() => deleteTableColumnAtSelection()}
          >
            {t('action:Delete')}
          </MenuButton>
        </MenuLine>
        {/* 머리: 「Add/Remove …」 글자 대신 켜짐·꺼짐. 종전 열 머리 글자는 키에 빈칸이 붙어
            「action:Add  column header」로 깨져 보였다 */}
        <MenuLine label={tl('header')}>
          <MenuButton
            pressed={hasRowHeader}
            title={headerTitle(hasRowHeader, 'row_header')}
            onClick={() => toggleTableRowIsHeader()}
          >
            {upperFirst(tl('row'))}
          </MenuButton>
          <MenuButton
            pressed={hasColumnHeader}
            title={headerTitle(hasColumnHeader, 'column_header')}
            onClick={() => toggleTableColumnIsHeader()}
          >
            {upperFirst(tl('column'))}
          </MenuButton>
        </MenuLine>
        <div className="mt-1 border-t border-gray-100 pt-1">
          <MenuButton
            danger
            title={tl('Delete_table')}
            onClick={() => deleteTableAtSelection()}
          >
            {tl('Delete_table')}
          </MenuButton>
        </div>
      </div>
    </div>,
    document.body
  );
}

const upperFirst = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

function MenuLine({
  label,
  count = 1,
  children,
}: {
  label: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="w-16 shrink-0 px-1 text-xs font-bold text-gray-500">
        {upperFirst(label)}
        {count > 1 ? ` ×${count}` : ''}
      </span>
      {children}
    </div>
  );
}

function MenuButton({
  title,
  onClick,
  danger = false,
  pressed,
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  pressed?: boolean;
  children: React.ReactNode;
}) {
  let tone = 'text-gray-800 hover:bg-gray-100';
  if (pressed) tone = 'bg-primary text-white hover:bg-primary-hover';
  else if (danger) tone = 'text-danger hover:bg-danger/10';
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={pressed}
      className={`whitespace-nowrap rounded-md px-2 py-1 ${tone}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function TableCellActionMenuContainer(): JSX.Element {
  const [editor] = useLexicalComposerContext();

  const menuButtonRef = useRef(null);
  const menuRootRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(
    null
  );
  const { t } = useTranslation(['toolbar', 'action']);

  const moveMenu = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (selection == null || menu == null) {
      setTableMenuCellNode(null);
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode()
      );

      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null);
        return;
      }

      const tableCellParentNodeDOM = editor.getElementByKey(
        tableCellNodeFromSelection.getKey()
      );

      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null);
        return;
      }

      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if (!activeElement) {
      setTableMenuCellNode(null);
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        moveMenu();
      });
    });
  });

  useEffect(() => {
    const menuButtonDOM = menuButtonRef.current as HTMLButtonElement | null;

    if (menuButtonDOM != null && tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey());

      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect();
        const menuRect = menuButtonDOM.getBoundingClientRect();

        menuButtonDOM.style.opacity = '1';

        menuButtonDOM.style.left = `${
          tableCellRect.left +
          window.pageXOffset -
          menuRect.width +
          tableCellRect.width -
          10
        }px`;

        menuButtonDOM.style.top = `${
          tableCellRect.top + window.pageYOffset + 5
        }px`;
      } else {
        menuButtonDOM.style.opacity = '0';
      }
    }
  }, [menuButtonRef, tableCellNode, editor]);

  const prevTableCellDOM = useRef(tableCellNode);

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }

    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);

  return (
    <div className="table-cell-action-button-container" ref={menuButtonRef}>
      {tableCellNode != null && (
        <>
          <button
            className="table-cell-action-button chevron-down"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            ref={menuRootRef}
            type="button"
          >
            <i className="chevron-down" />
          </button>
          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
              t={t}
            />
          )}
        </>
      )}
    </div>
  );
}

export default function TableActionMenuPlugin(): ReactPortal {
  return createPortal(<TableCellActionMenuContainer />, document.body);
}
